import { buildResourceMetaIndex, resourceLabel } from '@/api/permissionDisplay'
import type {
  Permission, PermissionResourceMeta, PermissionSensitivity,
} from '@/api/permissions'

export const OUTROS = 'Outros'

const SENSITIVITY_ORDER: Record<PermissionSensitivity, number> = {
  read: 0, write: 1, sensitive: 2, critical: 3,
}

export type MenuItemGroup = {
  key: string
  menu_item: string
  icon_key: string
  description: string
  sort_order: number
  perms: Permission[]
}

export type MenuSectionGroup = {
  section: string
  sort_order: number
  items: MenuItemGroup[]
}

function _semDiacritico(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '')
}

/**
 * Chave de agrupamento de uma seção: sem acento, caixa baixa, sem espaço nas pontas.
 *
 * `permission_resources.menu_section` é texto livre, e em 09/09/2026 tinha DUAS
 * grafias de Administração — `Administracao` (2 recursos) e `Administração` (8).
 * O grid agrupava pela string crua, então a tela mostrava dois accordions e dois
 * itens ficavam perdidos no menor. A MIGRATION_273 unifica o dado; agrupar pela
 * chave normalizada é o que impede a próxima linha digitada com grafia diferente
 * de abrir outra seção fantasma.
 *
 * Normalizar é para AGRUPAR. O rótulo exibido continua sendo o acentuado — ver
 * `_rotuloPreferido`.
 */
export function normalizeSectionKey(section: string): string {
  return _semDiacritico(section).trim().toLowerCase()
}

/** Tem acento? Só o diacrítico, sem mexer na caixa.
 *
 *  Comparar com `normalizeSectionKey` aqui seria errado — ela também abaixa a
 *  caixa, então QUALQUER rótulo capitalizado passaria por acentuado, o desempate
 *  cairia no alfabético e a tela mostraria 'Administracao'. Foi o que o teste
 *  pegou na primeira versão. */
function _temAcento(s: string): boolean {
  return s !== _semDiacritico(s)
}

/** Entre grafias que colapsam na mesma chave, qual mostrar.
 *
 *  Preferência: a que cobre mais permissões; empate, a que tem acento
 *  ('Administração', não 'Administracao'); empate ainda, ordem alfabética, para o
 *  resultado não depender da ordem de chegada. */
function _rotuloPreferido(variantes: Map<string, number>): string {
  return [...variantes.entries()]
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1]
      const acentoA = _temAcento(a[0]) ? 0 : 1
      const acentoB = _temAcento(b[0]) ? 0 : 1
      if (acentoA !== acentoB) return acentoA - acentoB
      return a[0].localeCompare(b[0])
    })[0][0]
}

/** Árvore seção → item → permissões, a partir do meta `resources`.
 *  Recurso sem meta cai em "Outros", que vai sempre por último. */
export function buildMenuTree(
  permissions: Permission[],
  resources: PermissionResourceMeta[],
): MenuSectionGroup[] {
  const metaByResource = buildResourceMetaIndex(resources)

  // chave normalizada → { variantes de rótulo, itens, menor sort_order }
  const sections = new Map<string, Map<string, MenuItemGroup>>()
  const variantes = new Map<string, Map<string, number>>()
  const sectionSort = new Map<string, number>()

  for (const p of permissions) {
    const meta = metaByResource.get(p.resource)
    const sectionRaw = meta?.menu_section || OUTROS
    const chave = normalizeSectionKey(sectionRaw)
    const menuItem = meta?.menu_item || resourceLabel(p.resource)
    const itemKey = `${chave}::${menuItem}`

    if (!variantes.has(chave)) variantes.set(chave, new Map())
    const v = variantes.get(chave)!
    v.set(sectionRaw, (v.get(sectionRaw) ?? 0) + 1)

    if (!sections.has(chave)) sections.set(chave, new Map())
    const items = sections.get(chave)!

    if (!items.has(itemKey)) {
      items.set(itemKey, {
        key: itemKey,
        menu_item: menuItem,
        icon_key: meta?.icon_key ?? '',
        description: meta?.description ?? '',
        sort_order: meta?.sort_order ?? Number.MAX_SAFE_INTEGER,
        perms: [],
      })
    }
    const grp = items.get(itemKey)!
    grp.perms.push(p)
    // menor sort_order do item domina; descrição/ícone vêm do menor
    if ((meta?.sort_order ?? Number.MAX_SAFE_INTEGER) < grp.sort_order) {
      grp.sort_order = meta?.sort_order ?? Number.MAX_SAFE_INTEGER
      if (meta?.icon_key) grp.icon_key = meta.icon_key
      if (meta?.description) grp.description = meta.description
    }

    const prev = sectionSort.get(chave)
    const cur = meta?.sort_order ?? Number.MAX_SAFE_INTEGER
    if (prev === undefined || cur < prev) sectionSort.set(chave, cur)
  }

  const result: MenuSectionGroup[] = []
  for (const [chave, items] of sections.entries()) {
    const itemGroups = [...items.values()]
    for (const ig of itemGroups) {
      ig.perms.sort((a, b) => {
        const sa = SENSITIVITY_ORDER[a.sensitivity] ?? 0
        const sb = SENSITIVITY_ORDER[b.sensitivity] ?? 0
        if (sa !== sb) return sa - sb
        return (a.label ?? a.action).localeCompare(b.label ?? b.action)
      })
    }
    itemGroups.sort((a, b) => a.sort_order - b.sort_order || a.menu_item.localeCompare(b.menu_item))
    const section = _rotuloPreferido(variantes.get(chave)!)
    result.push({
      section,
      sort_order: chave === normalizeSectionKey(OUTROS)
        ? Number.MAX_SAFE_INTEGER
        : (sectionSort.get(chave) ?? Number.MAX_SAFE_INTEGER),
      items: itemGroups,
    })
  }
  result.sort((a, b) => a.sort_order - b.sort_order || a.section.localeCompare(b.section))
  return result
}

/** Uma permissão casa a busca? Casa por seção, item, descrição do item, rótulo,
 *  action ou descrição da permissão — a mesma regra de antes da trilha. */
function _casa(
  sec: MenuSectionGroup, item: MenuItemGroup, p: Permission, q: string,
): boolean {
  return (
    sec.section.toLowerCase().includes(q) ||
    item.menu_item.toLowerCase().includes(q) ||
    item.description.toLowerCase().includes(q) ||
    (p.label ?? '').toLowerCase().includes(q) ||
    p.action.toLowerCase().includes(q) ||
    (p.description ?? '').toLowerCase().includes(q)
  )
}

/** A árvore reduzida ao que casa a busca. Busca vazia devolve a árvore intacta. */
export function filterTree(tree: MenuSectionGroup[], search: string): MenuSectionGroup[] {
  const q = search.trim().toLowerCase()
  if (!q) return tree
  return tree
    .map((sec) => ({
      ...sec,
      items: sec.items
        .map((item) => ({ ...item, perms: item.perms.filter((p) => _casa(sec, item, p, q)) }))
        .filter((item) => item.perms.length > 0),
    }))
    .filter((sec) => sec.items.length > 0)
}

/** Rótulos das seções que têm resultado para a busca.
 *
 *  Existe por causa da trilha: no accordion, uma busca abria todas as seções com
 *  resultado e o usuário via onde ele estava. Numa trilha só a seção ativa aparece,
 *  então a tela precisa DIZER onde estão os outros resultados — senão trocar de
 *  layout piora a busca. Busca vazia devolve conjunto vazio (nada a marcar). */
export function sectionsMatchingSearch(
  tree: MenuSectionGroup[], search: string,
): Set<string> {
  const q = search.trim().toLowerCase()
  if (!q) return new Set()
  return new Set(filterTree(tree, q).map((sec) => sec.section))
}

/** `selecionadas/total` por seção (chave = rótulo exibido).
 *
 *  Toda seção da árvore aparece no mapa, inclusive com zero selecionada: a trilha
 *  mostra "0/16", que informa, e não um espaço vazio, que não. */
export function countsBySection(
  tree: MenuSectionGroup[], selectedIds: Set<number>,
): Map<string, { selected: number; total: number }> {
  const out = new Map<string, { selected: number; total: number }>()
  for (const sec of tree) {
    let selected = 0
    let total = 0
    for (const item of sec.items) {
      for (const p of item.perms) {
        total += 1
        if (selectedIds.has(p.id)) selected += 1
      }
    }
    out.set(sec.section, { selected, total })
  }
  return out
}
